import { Link } from 'react-router';
import { LucideMenu, LucideX } from 'lucide-react';
import ProdegaLogo from '~/assets/logos/prodega-logo';
import TransgourmetLogo from '~/assets/logos/transgourmet-logo';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { Area } from '~/common/types/area';
import { PgArea, TgArea } from '~/common/types/area';
import { Button } from '~/common/components/ui/button';
import { LoginButton } from '~/shell/components/LoginButton';
import { MobileHeaderNavigation } from '~/shell/components/MobileHeaderNavigation';
import { cn } from '~/common/utils/styleUtil';
import { QuickSearch } from '~/features/catalog/components/quicksearch/QuickSearch';
import { useBasePath } from '~/common/hooks/useBasePath';
import { UnauthorizedCartButton } from '~/shell/components/UnauthorizedCartButton';
import { useScrollLock } from 'usehooks-ts';
import type { IWebshopLoginInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';

export interface MobileHeaderProps {
  area: Area;
  loginInfo?: IWebshopLoginInfoDtoData;
  isOciSession?: boolean;
  onMenuClick?: () => void;
  activeNavigation: 'menu' | 'customer' | 'templates' | null;
  onLogoClick: () => void;
  className?: string;
}

export type NavigationView = 'menu' | 'customer' | 'templates';

export function MobileHeader({
  className,
  area,
  loginInfo,
  isOciSession,
  onMenuClick,
  activeNavigation,
  onLogoClick
}: Readonly<MobileHeaderProps>) {
  const { t } = useTranslation();
  const { path } = useBasePath();
  const { lock, unlock } = useScrollLock({ autoLock: false });
  const [isOpen, setIsOpen] = useState(false);
  const isPg = area === PgArea;

  useEffect(() => {
    if (isOpen) {
      lock();
    } else {
      unlock();
    }
    return unlock();
  }, [isOpen, lock, unlock]);

  return (
    <div className={cn('p-4', isOpen ? 'bg-white' : 'bg-neutral-100', className)}>
      {!isOpen && (
        <>
          <div className={'flex justify-between items-center'}>
            <div className="flex flex-row items-center w-[260px] overflow-visible h-auto">
              <TrackNavigationClick label={'Home'}>
                <Link
                  to={path}
                  onClick={onLogoClick}
                  className={cn('w-[160px] overflow-visible h-auto', isPg && 'w-[120px]')}
                >
                  {isPg ? <ProdegaLogo /> : <TransgourmetLogo />}
                </Link>
              </TrackNavigationClick>
            </div>
          </div>
          <div className={'flex justify-between items-center'}>
            <Button variant={'ghost'} className={'flex pl-0 gap-1 items-center'} onClick={onMenuClick}>
              {activeNavigation ? <LucideX /> : <LucideMenu />}
              <div>{t('Menu')}</div>
            </Button>
            <div className={'flex'}>
              {loginInfo ? (
                <MobileHeaderNavigation loginInfo={loginInfo} isOciSession={isOciSession} />
              ) : (
                <LoginButton />
              )}
              {!loginInfo && area === TgArea && <UnauthorizedCartButton />}
            </div>
          </div>
        </>
      )}
      <QuickSearch
        className={'mt-2'}
        area={area}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        isAuthenticated={!!loginInfo}
      />
    </div>
  );
}
